/**
 * ProductCatalogModal - Modal para seleção de produtos/serviços
 * Catálogo completo com categorias, filtros e busca
 * 
 * Versão: 1.1.0
 * - Adicionado layout responsivo para mobile (fullscreen, sidebar colapsável)
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Package, Check, FolderTree } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCrmProducts } from '@/hooks/useCrmProducts';
import { useCrmCategories } from '@/hooks/useCrmCategories';
import { useIsMobile } from '@/hooks/use-mobile';
import { ProductCatalogHeader } from './ProductCatalogHeader';
import { CategorySidebar } from './CategorySidebar';
import { ProductGrid } from './ProductGrid';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

// Interface para item do deal (formato simplificado)
interface DealItemSimple {
  product_id: string;
  quantity: number;
}

// Props do componente
interface ProductCatalogModalProps {
  /** Controla se o modal está aberto */
  open: boolean;
  /** Callback para alterar o estado de abertura */
  onOpenChange: (open: boolean) => void;
  /** ID do deal */
  dealId: string;
  /** Moeda do deal */
  currency: string;
  /** ID do workspace para filtrar catálogo */
  workspaceId?: string | null;
  /** Itens atuais do deal */
  dealItems: DealItemSimple[];
  /** Handler para adicionar item */
  onAddItem: (productId: string, quantity: number, unitPrice: number) => Promise<void>;
  /** Handler para atualizar quantidade */
  onUpdateItemQuantity: (productId: string, quantity: number) => Promise<void>;
  /** Handler para remover item */
  onRemoveItem: (productId: string) => Promise<void>;
  /**
   * Quando true, filtra automaticamente apenas serviços recorrentes
   * (product_type='service' + unit_type='recorrente') e usa o recurring_price
   * como preço unitário ao adicionar. Esconde o seletor Produto/Serviço.
   * Usado pelo modal Nova Recorrência (Financeiro).
   */
  onlyRecurring?: boolean;
}


/**
 * Modal de catálogo de produtos
 * Permite selecionar e adicionar produtos/serviços ao deal
 */
export function ProductCatalogModal({
  open,
  onOpenChange,
  dealId,
  currency,
  workspaceId,
  dealItems,
  onAddItem,
  onUpdateItemQuantity,
  onRemoveItem,
  onlyRecurring = false,
}: ProductCatalogModalProps) {
  const isMobile = useIsMobile();

  // Estados de filtros
  const [selectedPriceTableId, setSelectedPriceTableId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  // Quando onlyRecurring=true, força product_type='service' e oculta o seletor
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'product' | 'service'>(
    onlyRecurring ? 'service' : 'all'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyTableProducts, setShowOnlyTableProducts] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  // Buscar produtos para contar por categoria (filtro workspace + unit_type quando aplicável)
  const { products } = useCrmProducts({
    is_active: true,
    workspace_id: workspaceId,
    product_type: onlyRecurring ? 'service' : null,
    unit_type: onlyRecurring ? 'recorrente' : null,
  }, { enabled: open });


  // Hook de categorias para obter IDs com subcategorias
  const { getCategoryWithDescendantIds } = useCrmCategories({ workspaceId });

  // Calcular IDs de categoria incluindo subcategorias
  const categoryIdsForFilter = useMemo(() => {
    if (!selectedCategoryId) return null;
    return getCategoryWithDescendantIds(selectedCategoryId);
  }, [selectedCategoryId, getCategoryWithDescendantIds]);

  // Calcular contagem de produtos por categoria
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    products.forEach((product) => {
      if (product.category_id) {
        counts[product.category_id] = (counts[product.category_id] || 0) + 1;
      }
    });
    return counts;
  }, [products]);

  // Handler para fechar modal
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Quantidade de itens selecionados
  const selectedCount = dealItems.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={`flex flex-col p-0 gap-0 ${
          isMobile 
            ? 'w-screen h-[100dvh] max-w-none rounded-none border-0' 
            : 'max-w-[95vw] h-[90vh]'
        }`}
        preventCloseOnOutsideInteraction
      >
        {/* Header do modal */}
        <DialogHeader className="p-3 md:p-4 pb-2 md:pb-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 md:p-2 rounded-lg bg-primary/10">
                <Package className="h-4 w-4 md:h-5 md:w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-sm md:text-base">Catálogo de Produtos</DialogTitle>
                <DialogDescription className="text-[10px] md:text-xs">
                  Selecione os produtos ou serviços para adicionar ao negócio
                </DialogDescription>
              </div>
            </div>
            {selectedCount > 0 && (
              <Badge variant="secondary" className="text-[10px] md:text-xs">
                {selectedCount} {selectedCount === 1 ? 'item' : 'itens'}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Corpo do modal */}
        <div className="flex-1 overflow-hidden flex flex-col p-2 md:p-4 pt-2 md:pt-3 gap-2 md:gap-3">
          {/* Cabeçalho: Tabela de Preço + Busca */}
          <ProductCatalogHeader
            selectedPriceTableId={selectedPriceTableId}
            onPriceTableChange={setSelectedPriceTableId}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            showOnlyTableProducts={showOnlyTableProducts}
            onShowOnlyTableProductsChange={setShowOnlyTableProducts}
            itemsCount={selectedCount}
            currency={currency}
            workspaceId={workspaceId}
          />

          {/* Área principal: Sidebar + Grid */}
          <div className={`flex-1 flex border border-border/50 rounded-lg overflow-hidden bg-background min-h-0 ${
            isMobile ? 'flex-col' : 'flex-row'
          }`}>
            {/* Mobile: Categorias colapsáveis */}
            {isMobile ? (
              <Collapsible open={showCategories} onOpenChange={setShowCategories}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full h-8 text-xs justify-between rounded-none border-b border-border/50"
                  >
                    <span className="flex items-center gap-1.5">
                      <FolderTree className="h-3.5 w-3.5" />
                      Categorias e Filtros
                    </span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      {selectedCategoryId ? '1 filtro' : 'Todos'}
                    </Badge>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="max-h-[40vh] overflow-y-auto">
                    <CategorySidebar
                      selectedCategoryId={selectedCategoryId}
                      onCategoryChange={(id) => {
                        setSelectedCategoryId(id);
                        setShowCategories(false);
                      }}
                      productTypeFilter={productTypeFilter}
                      onProductTypeChange={setProductTypeFilter}
                      categoryCounts={categoryCounts}
                      totalCount={products.length}
                      workspaceId={workspaceId}
                      isMobile
                      hideProductTypeFilter={onlyRecurring}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              /* Desktop: Sidebar lateral fixa */
              <CategorySidebar
                selectedCategoryId={selectedCategoryId}
                onCategoryChange={setSelectedCategoryId}
                productTypeFilter={productTypeFilter}
                onProductTypeChange={setProductTypeFilter}
                categoryCounts={categoryCounts}
                totalCount={products.length}
                workspaceId={workspaceId}
                hideProductTypeFilter={onlyRecurring}
              />
            )}

            {/* Grid de Produtos */}
            <ProductGrid
              categoryId={categoryIdsForFilter}
              productType={productTypeFilter}
              searchQuery={searchQuery}
              priceTableId={selectedPriceTableId}
              showOnlyTableProducts={showOnlyTableProducts}
              dealItems={dealItems}
              currency={currency}
              onAddItem={onAddItem}
              onUpdateItemQuantity={onUpdateItemQuantity}
              onRemoveItem={onRemoveItem}
              workspaceId={workspaceId}
              onlyRecurring={onlyRecurring}
            />

          </div>
        </div>

        {/* Footer do modal */}
        <div className="p-3 md:p-4 pt-2 md:pt-3 border-t shrink-0">
          <div className="flex items-center justify-end gap-2 md:gap-3">
            <Button variant="outline" size={isMobile ? 'sm' : 'default'} onClick={handleClose}>
              Cancelar
            </Button>
            <Button size={isMobile ? 'sm' : 'default'} onClick={handleClose} className="min-w-[100px] md:min-w-[120px]">
              <Check className="h-4 w-4 mr-2" />
              Concluir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
